import "./App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import AuthCallback from "./components/AuthCallback";
import Home from "./pages/Home";

const Services = lazy(() => import("./pages/Services"));
const BundlePage = lazy(() => import("./pages/BundlePage"));
const Careers = lazy(() => import("./pages/Careers"));
const AdminRequests = lazy(() => import("./pages/AdminRequests"));
const AdminTickets = lazy(() => import("./pages/AdminTickets"));
const AdminCareers = lazy(() => import("./pages/AdminCareers"));
const AdminReviews = lazy(() => import("./pages/AdminReviews"));
const AdminSandbox = lazy(() => import("./pages/AdminSandbox"));
const AdminPartnerTickets = lazy(() => import("./pages/AdminPartnerTickets"));
const AdminFeedback = lazy(() => import("./pages/AdminFeedback"));
const AdminReports = lazy(() => import("./pages/AdminReports"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Reviews = lazy(() => import("./pages/Reviews"));
const SeoTools = lazy(() => import("./pages/SeoTools"));
const PaymentReturn = lazy(() => import("./pages/PaymentReturn"));
const Templates = lazy(() => import("./pages/Templates"));
const TemplateDetail = lazy(() => import("./pages/TemplateDetail"));
const TemplatePreview = lazy(() => import("./pages/TemplatePreview"));
const Cart = lazy(() => import("./pages/Cart"));
const AreasIndex = lazy(() => import("./pages/AreasIndex"));
const AreaPage = lazy(() => import("./pages/AreaPage"));
const SolutionsIndex = lazy(() => import("./pages/SolutionsIndex"));
const SolutionHub = lazy(() => import("./pages/SolutionHub"));
const SolutionPage = lazy(() => import("./pages/SolutionPage"));
const ResourceHub = lazy(() => import("./pages/ResourceHub"));
const ResourceHubPage = lazy(() => import("./pages/ResourceHubPage"));
const Growth = lazy(() => import("./pages/Growth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AiGrowthCenter = lazy(() => import("./pages/AiGrowthCenter"));
const TemplateRecommender = lazy(() => import("./pages/TemplateRecommender"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminHub = lazy(() => import("./pages/AdminHub"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminContentStudio = lazy(() => import("./pages/AdminContentStudio"));
const AdminOutreach = lazy(() => import("./pages/AdminOutreach"));
const AdminHotLeads = lazy(() => import("./pages/AdminHotLeads"));
const AdminSequences = lazy(() => import("./pages/AdminSequences"));
const AdminPartners = lazy(() => import("./pages/AdminPartners"));
const Partners = lazy(() => import("./pages/Partners"));
const PartnerLogin = lazy(() => import("./pages/PartnerLogin"));
const PartnerVerify = lazy(() => import("./pages/PartnerVerify"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const AccountLogin = lazy(() => import("./pages/AccountLogin"));
const AccountVerify = lazy(() => import("./pages/AccountVerify"));
const AccountPortal = lazy(() => import("./pages/AccountPortal"));
const AcceleratorPlans = lazy(() => import("./pages/AcceleratorPlans"));
const GrowthPlanDetail = lazy(() => import("./pages/GrowthPlanDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const CartSuccess = lazy(() => import("./pages/CartSuccess"));
const AcceleratorSuccess = lazy(() => import("./pages/AcceleratorSuccess"));
const AdminSubscriptions = lazy(() => import("./pages/AdminSubscriptions"));
const AdminRecommendations = lazy(() => import("./pages/AdminRecommendations"));
const WebsiteBuild = lazy(() => import("./pages/WebsiteBuild"));
const AdminBuilds = lazy(() => import("./pages/AdminBuilds"));
const AdminMonitors = lazy(() => import("./pages/AdminMonitors"));
const AdminAds = lazy(() => import("./pages/AdminAds"));
const AdminSocial = lazy(() => import("./pages/AdminSocial"));
const AdminTeam = lazy(() => import("./pages/AdminTeam"));
const AdminVideoExport = lazy(() => import("./pages/AdminVideoExport"));
const AdminRecovery = lazy(() => import("./pages/AdminRecovery"));
const AdminTailored = lazy(() => import("./pages/AdminTailored"));
const AdminCollections = lazy(() => import("./pages/AdminCollections"));
const AdminPipeline = lazy(() => import("./pages/AdminPipeline"));
const AdminBlogRewrite = lazy(() => import("./pages/AdminBlogRewrite"));
const PreviewPage = lazy(() => import("./pages/PreviewPage"));
const PayBuyout = lazy(() => import("./pages/PayBuyout"));
const AdApproval = lazy(() => import("./pages/AdApproval"));
const Terms = lazy(() => import("./pages/Terms"));

const Fallback = () => (
  <div className="mx-auto max-w-4xl px-6 py-24 text-center text-muted-foreground">Loading…</div>
);

function AppRoutes() {
  const location = useLocation();
  // Process OAuth return FIRST (session_id lives in the URL fragment).
  if (location.hash?.includes("session_id=")) {
    return <Suspense fallback={<Fallback />}><AuthCallback /></Suspense>;
  }
  return (
    <Suspense fallback={<Fallback />}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/ai-business-growth" element={<AiGrowthCenter />} />
          <Route path="/template-recommender" element={<TemplateRecommender />} />
          <Route path="/growth" element={<Growth />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/admin" element={<AdminHub />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/content" element={<AdminContentStudio />} />
          <Route path="/admin/outreach" element={<AdminOutreach />} />
          <Route path="/admin/hot-leads" element={<AdminHotLeads />} />
          <Route path="/admin/sequences" element={<AdminSequences />} />
          <Route path="/admin/partners" element={<AdminPartners />} />
          <Route path="/admin/requests" element={<AdminRequests />} />
          <Route path="/admin/tickets" element={<AdminTickets />} />
          <Route path="/admin/careers" element={<AdminCareers />} />
          <Route path="/admin/reviews" element={<AdminReviews />} />
          <Route path="/admin/sandbox" element={<AdminSandbox />} />
          <Route path="/admin/partner-tickets" element={<AdminPartnerTickets />} />
          <Route path="/admin/feedback" element={<AdminFeedback />} />
          <Route path="/admin/reports" element={<AdminReports />} />
          <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
          <Route path="/admin/recommendations" element={<AdminRecommendations />} />
          <Route path="/admin/builds" element={<AdminBuilds />} />
          <Route path="/admin/monitors" element={<AdminMonitors />} />
          <Route path="/admin/ads" element={<AdminAds />} />
          <Route path="/admin/social" element={<AdminSocial />} />
          <Route path="/admin/video" element={<AdminVideoExport />} />
          <Route path="/admin/team" element={<AdminTeam />} />
          <Route path="/admin/recovery" element={<AdminRecovery />} />
          <Route path="/admin/tailored" element={<AdminTailored />} />
          <Route path="/admin/collections" element={<AdminCollections />} />
          <Route path="/admin/pipeline" element={<AdminPipeline />} />
          <Route path="/admin/blog-rewrite" element={<AdminBlogRewrite />} />
          <Route path="/preview/:id" element={<PreviewPage />} />
          <Route path="/pay-buyout" element={<PayBuyout />} />
          <Route path="/website-build" element={<WebsiteBuild />} />
          <Route path="/ad-approval/:token" element={<AdApproval />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/partner-login" element={<PartnerLogin />} />
          <Route path="/partner/verify" element={<PartnerVerify />} />
          <Route path="/partner" element={<PartnerDashboard />} />
          <Route path="/account-login" element={<AccountLogin />} />
          <Route path="/account/verify" element={<AccountVerify />} />
          <Route path="/account" element={<AccountPortal />} />
          <Route path="/accelerator" element={<AcceleratorPlans />} />
          <Route path="/growth-plans/:planKey" element={<GrowthPlanDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/cart/success" element={<CartSuccess />} />
          <Route path="/accelerator/success" element={<AcceleratorSuccess />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/grow/:slug" element={<BundlePage />} />
          <Route path="/grow/:slug/:location" element={<BundlePage />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/:slug" element={<ProductDetail />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/templates/:slug/preview" element={<TemplatePreview />} />
          <Route path="/templates/:slug" element={<TemplateDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/seo-tools" element={<SeoTools />} />
          <Route path="/book-call" element={<Navigate to="/get-started" replace />} />
          <Route path="/payment-return" element={<PaymentReturn />} />
          <Route path="/areas" element={<AreasIndex />} />
          <Route path="/areas/:slug" element={<AreaPage />} />
          <Route path="/solutions" element={<SolutionsIndex />} />
          <Route path="/solutions/:service" element={<SolutionHub />} />
          <Route path="/solutions/:service/:industry" element={<SolutionPage />} />
          <Route path="/resources" element={<ResourceHub />} />
          <Route path="/resources/:industry" element={<ResourceHubPage />} />
          {/* Legacy SEO URLs preserved: WordPress dated permalinks + old .html pages */}
          <Route path="/:y/:m/:d/:postSlug" element={<BlogPost />} />
          <Route path="/:legacySlug" element={<BlogPost />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <CartProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </CartProvider>
    </ThemeProvider>
  );
}

export default App;
